import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, ChevronLeft, ChevronRight, Save } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { CARD, Label, Loader } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const MOIS = ["", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const hm = (h) => { const m = Math.round(h * 60); return `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}`; };
const shift = (ym, n) => { const d = new Date(+ym.slice(0, 4), +ym.slice(5, 7) - 1 + n, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const DEFAULT_REC = { name: "Anne-Hélène FAUSSOU RICHOME", email: "ahfaussou@synaaps.fr" };

export function HrReleve() {
  const [ym, setYm] = useState(() => new Date().toISOString().slice(0, 7));
  const [d, setD] = useState(null);
  const [rec, setRec] = useState({ name: "", email: "" });
  const load = useCallback(() => {
    setD(null);
    api.get("/hr/releve", { params: { month: ym } }).then((r) => { setD(r.data); setRec(r.data.recipient.name || r.data.recipient.email ? r.data.recipient : DEFAULT_REC); }).catch((e) => toast.error(apiErr(e)));
  }, [ym]);
  useEffect(() => { load(); }, [load]);
  const saveRec = () => api.put("/hr/releve/recipient", rec).then(() => toast.success("Destinataire enregistré")).catch((e) => toast.error(apiErr(e)));
  const pdf = async () => {
    try {
      await api.put("/hr/releve/recipient", rec);
      const r = await api.get("/hr/releve/pdf", { params: { month: ym }, responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `releve_heures_${ym}.pdf`; a.click(); URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className={`${CARD} mt-6 space-y-4 p-5 sm:p-6`} data-testid="hr-releve">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Label>Relevé du mois pour la mission sociale</Label>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => setYm(shift(ym, -1))} aria-label="Mois précédent" data-testid="releve-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="w-32 text-center text-sm font-semibold" data-testid="releve-month">{MOIS[+ym.slice(5, 7)]} {ym.slice(0, 4)}</span>
          <Button variant="ghost" size="icon" onClick={() => setYm(shift(ym, 1))} aria-label="Mois suivant" data-testid="releve-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Input value={rec.name} onChange={(e) => setRec({ ...rec, name: e.target.value })} placeholder="Nom du contact paie" className="w-64" data-testid="releve-rec-name" />
        <Input value={rec.email} onChange={(e) => setRec({ ...rec, email: e.target.value })} placeholder="E-mail" className="w-64" data-testid="releve-rec-email" />
        <Button variant="outline" onClick={saveRec} data-testid="releve-rec-save"><Save className="mr-1.5 h-4 w-4" />Enregistrer</Button>
      </div>
      {!d ? <Loader /> : (
        <>
          {d.holidays.length > 0 && <p className="text-xs text-amber-700" data-testid="releve-holidays">Jours fériés : {d.holidays.map((h) => `${h.date.slice(8, 10)}/${h.date.slice(5, 7)} ${h.name}`).join(" · ")}</p>}
          <div className="overflow-x-auto"><table className="w-full min-w-[560px] text-sm" data-testid="releve-table">
            <thead><tr className="border-b border-gray-100 text-left text-xs text-gray-400"><th className="py-2">Salarié</th><th className="text-right">Heures</th><th className="text-right">dont fériés</th><th className="text-right">Au-delà du contrat</th><th className="pl-4">Congés / absences</th></tr></thead>
            <tbody className="divide-y divide-gray-50">{d.employees.map((e) => (
              <tr key={e.employee_id}><td className="py-2 font-medium">{e.name}</td><td className="text-right tabular-nums">{hm(e.hours)}</td>
                <td className="text-right tabular-nums">{e.holiday_hours ? hm(e.holiday_hours) : "—"}</td><td className="text-right tabular-nums">{e.over_hours ? hm(e.over_hours) : "—"}</td>
                <td className="pl-4 text-xs text-gray-500">{Object.entries(e.absences).map(([k, v]) => `${k} : ${v} j`).join(", ") || "—"}</td></tr>))}</tbody>
          </table></div>
          {!d.employees.length && <p className="text-sm text-gray-400">Aucun salarié actif.</p>}
          <Button onClick={pdf} className="rounded-full bg-[#065F46] hover:bg-[#054d39]" data-testid="releve-pdf"><FileDown className="mr-1.5 h-4 w-4" />Télécharger le relevé PDF</Button>
          <p className="text-xs text-gray-400">Un rappel arrive le 25 de chaque mois (notification et e-mail). « Au-delà du contrat » est indicatif, calculé semaine par semaine.</p>
        </>)}
    </div>
  );
}
